import { applyAuthDashboardLocales } from "./localeAuthDashboardBlocks";
import { applyAiDashboardGoalAndVitalsLocales } from "./localeAiDashboardGoalBlocks";
import { applyAuthContactLocales } from "./localeAuthContactBlocks";
import { applyBreadcrumbLocaleBlocks } from "./localeBreadcrumbBlocks";
import { applyCalendarLocaleBlocks } from "./localeCalendarBlocks";
import { applyHelpGuideLocaleBlocks } from "./localeHelpGuideBlocks";
import { applyMedCloudLocaleBlocks } from "./localeMedCloudBlocks";
import { applyMedicationFormLocaleBlocks } from "./localeMedicationFormBlocks";
import { applyNavLocaleBlocks } from "./localeNavBlocks";
import { applyReportsLocaleBlocks } from "./localeReportsBlocks";
import { applySettingsLocaleBlocks } from "./localeSettingsBlocks";
import { applyTermsLocaleBlocks, EN_TERMS_KEY_POINTS } from "./localeTermsBlocks";
import { applyFamilyPageLocales } from "./familyPageLocales";
import { applyRelationshipTypeLocales } from "./localeRelationshipBlocks";
import {
  EN_AUTH_MODAL_CREDENTIAL,
  EN_COMMON_CREDENTIAL,
  EN_LOGIN_CREDENTIAL,
  EN_SETTINGS_CREDENTIAL,
  buildChangeCredentialLabel,
  buildLoginCredentialLabels,
  buildSwitchCredentialLoginLabel,
} from "./credentialUiLabels";

export const LANGUAGE_OPTIONS = [
  { value: "en", label: "English" },
  { value: "hi", label: "हिंदी" },
  { value: "pa", label: "ਪੰਜਾਬੀ" },
  { value: "te", label: "తెలుగు" },
  { value: "ta", label: "தமிழ்" },
  { value: "mr", label: "मराठी" },
  { value: "bn", label: "বাংলা" },
  { value: "kn", label: "ಕನ್ನಡ" },
];

/** BCP-47 locales for Web Speech API (mic input). */
export const SPEECH_RECOGNITION_LOCALE = {
  en: "en-IN",
  hi: "hi-IN",
  pa: "pa-IN",
  te: "te-IN",
  ta: "ta-IN",
  mr: "mr-IN",
  bn: "bn-IN",
  kn: "kn-IN",
};

export const getSpeechRecognitionLocale = (languageCode) =>
  SPEECH_RECOGNITION_LOCALE[languageCode] || SPEECH_RECOGNITION_LOCALE.en;

export const TRANSLATIONS = {
  en: {
    common: {
      language: "Language",
      selectLanguage: "Select Language",
      login: "Login",
      registerNow: "Register Now",
      profile: "Profile",
      ...EN_COMMON_CREDENTIAL,
      logout: "Logout",
      retry: "Retry",
      notifications: "Notifications",
      helpGuide: "Help Guide",
      self: "Self",
      familyMember: "Family Member",
      applyingLanguage: "Updating language…",
      loading: "Loading…",
      back: "Back",
      update: "Update",
      cancel: "Cancel",
      delete: "Delete",
      healthInterests: "Health Interests",
      selectedMember: "Selected member",
    },
    breadcrumb: {
      brand: "myTara",
      familyMembers: "Family Members",
      addMedication: "Add Medication",
      medicationInformation: "Medication Information",
      medicationList: "Medication List",
      fullChatLogs: "Full Chat Logs",
      rescheduleAppointment: "Reschedule Appointment",
      appointmentList: "Appointment List",
      bookAppointment: "Book Appointment",
      profileSettings: "Profile Setting",
      profile: "Profile",
      rewards: "Rewards",
      coinWallet: "Coin Wallet",
      legal: "Legal",
      doctor: "Doctor",
      doctorDashboard: "Dashboard",
      viewFile: "View File",
    },
    settings: {
      title: "Settings",
      heroTitle: "Account Settings",
      intro: "Manage your account security, legal documents, and account status.",
      ...EN_SETTINGS_CREDENTIAL,
      termsAndConditions: "Terms and Conditions",
      termsLink: "Terms & Conditions",
      termsLinkDesc: "View the full legal document",
      readFullTerms: "Read the full Terms & Conditions",
      accountActions: "Account actions",
      saveChanges: "Save Changes",
      deleteAccount: "Delete Account",
      deleteAccountBadge: "15-DAY RECOVERY WINDOW",
      deleteAccountDesc:
        "Permanently delete your parent account and all linked family profiles. You have a 15-day recovery window to sign back in and restore everything.",
    },
    terms: {
      beforeContinue: "Before you continue",
      agreeingTo: "Here's what you're agreeing to",
      keyPointsAria: "Key points summary",
      keyPointsTitle: "Key Points",
      readFullTerms: "Read the full Terms & Conditions",
      decline: "Decline",
      accept: "Accept",
      sections: "Sections",
      servicesTitle: "Terms of Services",
      summaryAria: "Terms and conditions summary",
      summaryTitle: "MyTara™ Terms and Conditions",
      readFullDocument: "Read full Terms & Conditions",
      registerConsent:
        "I have read the summarized Terms & Conditions and agree to register.",
      fullDocumentLink: "View full Terms & Conditions",
      fullDocumentHint: "Legal document · Read online",
      reachedBottom: "You have reached the end",
      scrollToBottom: "Scroll to bottom",
      nextSection: "Next section",
      translating: "Translating terms...",
      scrollKeyPointsRequired:
        "Please read all key points and scroll to the bottom before accepting.",
      acceptFailed: "Could not save your acceptance. Please try again.",
      documentTagline: "Please review each section carefully before accepting.",
      documentDetails: "Document details",
      documentVersion: "Version",
      documentPages: "pages",
      documentViewOnline: "View online only",
      documentNotice:
        "This document is for online viewing only. Downloading, printing, screen capture, and snipping-tool reproduction are restricted. Unauthorized copying may violate applicable law and your agreement with myTara.",
      keyPoints: EN_TERMS_KEY_POINTS,
    },
    helpGuide: {
      subtitle: "FAQs • Tips • Support",
      subtitleFaq: "FAQs",
      subtitleTips: "Tips",
      subtitleSupport: "Support",
      searchPlaceholder: "Search help articles...",
      categories: "Categories",
      categoryAll: "All",
      categoryDailyHealth: "Dashboard & Vitals",
      categoryAiAssistant: "Tara AI Chat",
      categorySecurity: "Records & Meds",
      welcomeTitle: "Welcome to myTara Help Center",
      welcomeDesc:
        "Complete guides for Dashboard, vitals, Tara AI chat, medications, reports, Med Cloud, family profiles, coins, calendar, and account settings.",
      dailyHealthTitle: "Dashboard & Live Vitals",
      dailyHealthSubtitle: "Your daily health hub with real-time vitals, charts, and wellness tools",
      dailyHealthStep1: "Open Dashboard from the sidebar to see greeting, vitals cards, and health stats",
      dailyHealthStep2: "Monitor blood pressure, heart rate, glucose, and SpO₂ with live socket updates",
      dailyHealthStep3: "Switch between BP, heart rate, glucose, and oxygen charts on the dashboard",
      dailyHealthStep4: "Mark medicines as taken directly from the dashboard medication section",
      dailyHealthStep5: "Watch fitness and wellness goal videos suggested for your health profile",
      dailyHealthStep6: "Complete Daily Missions on the dashboard to earn coins and stay on track",
      dailyHealthTip: "Tip: Check your dashboard each morning for vitals alerts like low SpO₂ warnings",
      vitalsReportsTitle: "Vitals Reports",
      vitalsReportsSubtitle: "View trends, history, and detailed readings over time",
      vitalsReportsBullet1: "Open Vitals Reports from the sidebar for historical health data",
      vitalsReportsBullet2: "Compare readings across dates to spot patterns in BP, HR, glucose, and SpO₂",
      vitalsReportsBullet3: "Use reports when discussing trends with your doctor or family caregiver",
      vitalsReportsBullet4: "Ask Tara in chat to summarize your recent vitals using Quick Health Actions",
      calendarTitle: "Calendar & Appointments",
      calendarSubtitle: "Schedule visits and keep track of upcoming health events",
      calendarBullet1: "Open Calendar from the sidebar to view and manage appointments",
      calendarBullet2: "Book new appointments and reschedule existing visits when needed",
      calendarBullet3: "Use Tara chat Quick Picks → Appointments to see upcoming visits quickly",
      calendarBullet4: "Complete the health questionnaire for family members before appointments",
      calendarTip: "Tip: Fill the health questionnaire early so your doctor has full context",
      coinsTitle: "Coins, Rewards & Missions",
      coinsSubtitle: "Earn coins for healthy habits and redeem rewards",
      coinsBullet1: "See your coin balance in the Rewards strip below the header after login",
      coinsBullet2: "Earn +1 coin on every login and more by completing daily missions",
      coinsBullet3: "Tap Balance or Redeem to open your Coin Wallet page",
      coinsBullet4: "Complete dashboard and wallet missions to boost your reward balance",
      aiTitle: "Tara AI Chat & Quick Picks",
      aiSubtitle: "24/7 health assistant with smart shortcuts for common tasks",
      aiBullet1: "Tap the chat bubble (bottom-right) to open Tara and ask health questions",
      aiBullet2: "Quick Picks: Check Symptoms, Vital Reports, Medication, Reports, Health Summary",
      aiBullet3: "Quick Picks also include Family Member and Appointments for faster navigation",
      aiBullet4: "Use voice, text, or file upload in chat for symptoms and report analysis",
      aiBullet5: "Guest users on the Home page can also chat with Tara before registering",
      taraGuideTitle: "Tara Guide Side Panel",
      taraGuideSubtitle: "Contextual AI guidance while you browse the app",
      taraGuideBullet1: "The Tara Guide panel stays open on the right side while you use myTara",
      taraGuideBullet2: "Get health alerts such as low SpO₂ warnings and personalized tips",
      taraGuideBullet3: "Read Tara's messages and reply using text or voice input in the panel",
      taraGuideBullet4: "Use it alongside the main chat for continuous health support",
      myTaraReportsTitle: "MyTara Reports & AI Summaries",
      myTaraReportsSubtitle: "AI-generated chat summaries and health conversation logs",
      myTaraReportsBullet1: "Go to Reports → MyTara Reports in the sidebar for AI chat summaries",
      myTaraReportsBullet2: "Review past conversations and health insights generated by Tara",
      myTaraReportsBullet3: "Open individual summary logs for detailed session history",
      myTaraReportsBullet4: "Use Health Summary Quick Pick in chat for a quick wellness overview",
      familyTitle: "Family Search & Profiles",
      familySubtitle: "Manage family members and switch between health profiles",
      familyBullet1: "Open Family Search from the sidebar to add or edit family members",
      familyBullet2: "Switch between Self and family profiles using the header profile dropdown",
      familyBullet3: "Each family member can have their own vitals, meds, and reports",
      familyBullet4: "Complete the health questionnaire when switching to a new family profile",
      medicationTitle: "Medication & Reminders",
      medicationSubtitle: "Track prescriptions, doses, and never miss a medicine",
      medicationBullet1: "Open Medication from the sidebar to view all active prescriptions",
      medicationBullet2: "Add new medicines with dose times and enable reminders for each schedule",
      medicationBullet3: "Mark doses as taken from the dashboard or medication detail page",
      medicationBullet4: "Use Tara chat Quick Pick → Medication to check prescriptions quickly",
      medicationTip: "Tip: Set reminders at the same time you take meals for better consistency",
      medCloudTitle: "Med Cloud Storage",
      medCloudSubtitle: "Securely store prescriptions, lab reports, and medical documents",
      medCloudBullet1: "Open Med Cloud from the sidebar to upload and organize health files",
      medCloudBullet2: "Store prescriptions, lab results, scans, and other medical documents",
      medCloudBullet3: "Access uploaded files anytime from the same Med Cloud page",
      medCloudBullet4: "Share documents with Tara chat for AI-powered report understanding",
      medicalReportsTitle: "Medical Reports",
      medicalReportsSubtitle: "View, download, and manage your official health records",
      medicalReportsBullet1: "Go to Reports → Medical Reports in the sidebar for your health records",
      medicalReportsBullet2: "View lab reports, prescriptions, and clinical documents in one place",
      medicalReportsBullet3: "Download or share reports when visiting doctors or specialists",
      medicalReportsBullet4: "Use Reports Quick Pick in Tara chat to jump to your records faster",
      accountTitle: "Account, Profile & App Settings",
      accountSubtitle: "Header tools, security, language, theme, and notifications",
      accountBullet1: "Profile menu: credential update, Health Interests, and Logout",
      accountBullet2: "Pick Health Interests after login to personalize your myTara experience",
      accountBullet3: "Bell icon shows notifications; language selector changes app language",
      accountBullet4: "? Help Guide, fullscreen toggle, and light/dark theme switch in header",
      accountBullet5: "Login with Remember Me to stay signed in across browser sessions",
      accountBullet6: "Complete the Initial Health Questionnaire from profile or family pages",
      accountTip: "Tip: Update Health Interests anytime from the profile menu to refine AI tips",
      faqTitle: "Frequently Asked Questions",
      faqMetricsQ: "How do I check my vitals on the dashboard?",
      faqMetricsA:
        "Open Dashboard from the sidebar. Live vitals for BP, heart rate, glucose, and SpO₂ appear on the main cards with charts. For full history, go to Vitals Reports.",
      faqVitalsReportsQ: "Where can I see my vitals history and trends?",
      faqVitalsReportsA:
        "Open Vitals Reports from the sidebar. You can review past readings and trends for blood pressure, heart rate, glucose, and oxygen levels.",
      faqAppointmentsQ: "How do I book or view appointments?",
      faqAppointmentsA:
        "Go to Calendar in the sidebar to view, book, or reschedule appointments. You can also ask Tara via Quick Picks → Appointments.",
      faqCoinsQ: "How do coins and daily missions work?",
      faqCoinsA:
        "You earn coins on login and by completing daily missions shown on the Dashboard and Coin Wallet. Check your balance in the Rewards strip and tap Redeem to open Coin Wallet.",
      faqAiQ: "How do I use Tara chat and Quick Picks?",
      faqAiA:
        "Tap the chat icon at the bottom-right. Use Quick Picks for Check Symptoms, Vital Reports, Medication, Reports, Health Summary, Family Member, and Appointments. Type or speak your health questions anytime.",
      faqTaraGuideQ: "What is the Tara Guide side panel?",
      faqTaraGuideA:
        "Tara Guide is the panel on the right side of the app. It shows health alerts, Tara messages, and lets you chat while browsing Dashboard, Medication, Reports, and other pages.",
      faqMyTaraReportsQ: "What is the difference between Medical Reports and MyTara Reports?",
      faqMyTaraReportsA:
        "Medical Reports contain your official health records and lab documents. MyTara Reports contain AI-generated summaries of your Tara chat conversations and health insights.",
      faqFamilyQ: "How do I add or switch family member profiles?",
      faqFamilyA:
        "Add members in Family Search from the sidebar. Switch profiles using the Self / Family Member dropdown in the header. Complete the health questionnaire for each new profile.",
      faqMedCloudQ: "How do I upload documents to Med Cloud?",
      faqMedCloudA:
        "Open Med Cloud from the sidebar, upload prescriptions, lab reports, or scans, and access them anytime. You can also share files with Tara for AI analysis in chat.",
      faqMedsQ: "How do medication reminders work?",
      faqMedsA:
        "Go to Medication, add your prescription with dose times, and enable reminders. Mark doses as taken from the dashboard or medication page when you take them.",
      faqQuestionnaireQ: "What is the health questionnaire?",
      faqQuestionnaireA:
        "The Initial Health Questionnaire collects basic health info, medications, family history, and goals. Complete it from your profile or when adding a family member for better AI personalization.",
      faqChangeCredentialQ: "How do I update my sign-in credentials?",
      faqChangeCredentialA:
        "Click your profile avatar in the header, open the profile menu, and choose the credential update option. Enter your current sign-in details, then set new ones.",
      faqHealthInterestsQ: "How do I update my health interests?",
      faqHealthInterestsA:
        "Open the profile menu and select Health Interests. Pick topics like vitals tracking, medicine reminders, or AI health analysis to personalize tips and dashboard content.",
      faqLanguageThemeQ: "How do I change language or switch light/dark theme?",
      faqLanguageThemeA:
        "Use the language selector in the header to change app language. Use the theme toggle button to switch between light and dark mode. Both settings apply across the entire app.",
      faqNotificationsQ: "How do notifications work?",
      faqNotificationsA:
        "Tap the bell icon in the header to view alerts about health updates, appointments, and app activity. Unread notifications are highlighted until you open them.",
      faqRememberMeQ: "What does Remember Me do on login?",
      faqRememberMeA:
        "When checked, Remember Me keeps you signed in across browser sessions using secure storage. Without it, you will need to log in again when you close the browser.",
      noResults: "No help articles match your search.",
      seeMoreGuides: "See more guides",
      seeMoreFaqs: "See more questions",
      showLess: "Show less",
    },
    healthInterests: {
      title: "Pick Your Health Interests",
      subtitle:
        "Select what matters most to you — you can change this anytime from your profile menu.",
      continue: "Continue",
      save: "Save interests",
      close: "Close",
      pickAtLeastOne: "Pick at least one health interest to continue.",
      infoHint: "Hover or tap an interest to see what it includes.",
      dailyExerciseInfo:
        "Get workout plans, activity reminders, and tips to stay active every day.",
      peaceOfMindInfo:
        "Meditation, breathing exercises, and stress relief guidance for mental wellness.",
      healthyEatingInfo:
        "Meal ideas, nutrition tips, and diet tracking to support balanced eating.",
      doctorCheckupsInfo:
        "Book visits, follow-up reminders, and keep your checkup schedule on track.",
      medicineRemindersInfo:
        "Never miss a dose — alerts and schedules for your medicines.",
      vitalsTrackingInfo:
        "Log blood pressure, heart rate, glucose, and more in one place.",
      aiHealthAnalysisInfo:
        "AI-powered health scans and insights from your reports and symptoms.",
      liveHealthInsightsInfo:
        "Real-time trends and alerts based on your latest health data.",
      healthImprovementInfo:
        "Set wellness goals and track progress toward a healthier lifestyle.",
      smartReportInfo:
        "Smart summaries of lab reports and health records, easy to understand.",
      saved: "Your health interests were updated.",
      savedWelcome: "Great! We will personalize your experience.",
      loginRequired: "Please sign in again.",
      loadFailed:
        "Could not load health interests. Please close and try again.",
      saveFailed: "Could not save your health interests. Please try again.",
      saving: "Saving...",
      dailyExercise: "Daily Exercise",
      peaceOfMind: "Peace of Mind",
      healthyEating: "Healthy Eating",
      doctorCheckups: "Doctor Visits",
      medicineReminders: "Medicine Alerts",
      vitalsTracking: "Track Vitals",
      aiHealthAnalysis: "AI Health Scan",
      liveHealthInsights: "Live Insights",
      healthImprovement: "Wellness Goals",
      smartReport: "Smart Reports",
    },
    questionnaire: {
      title: "Initial Health Questionnaire",
      basicInfo: "Basic Info",
      emergency: "Emergency",
      healthOverview: "Health Overview",
      medications: "Medications",
      familyHistory: "Family History",
      goals: "Goals",
      finalInput: "Final Input",
    },
    aiDashboard: {
      goodMorning: "Good Morning",
      goodAfternoon: "Good Afternoon",
      goodEvening: "Good Evening",
      takeCharge: "Take charge of your health today!",
      healthStatsToday: "Here's what's happening with your health stats today.",
      secureRecords: "Secure your Health Records",
      checkVitals: "Check Your Vital Signs",
      uploadVital: "Upload Vital",
      vitalsReports: "Vitals Reports",
      uploadReports: "Upload Reports",
      taraUploadHint: "Upload a report from Dashboard or attach a file here to see analysis.",
      analysisPleaseWait: "Analysis please wait…",
      analyzingStatus: "analyzing",
      analysisHint: "We are reading your report. This may take a moment.",
      duplicateReportNotSaved: "This document is already in Med Cloud. Duplicate file was not saved.",
      reportSavedToMedCloud: "{name} saved to Med Cloud.",
      reportMedCloudSaveFailed: "Report analyzed but Med Cloud save failed.",
      reportAnalyzeFailed: "Failed to analyze report. Please try again.",
      reportUploadLoginRequired: "Please log in again to upload reports.",
      openMedCloud: "Open Med Cloud",
      today: "Today",
      average: "Average",
      medicineTracker: "Medicine Tracker",
      allTasks: "All Tasks",
      noMedicines: "No medicines available",
      upcoming: "Upcoming",
      taken: "Taken",
      missed: "Missed",
      markTaken: "Mark Taken",
      details: "Details",
      scheduled: "Scheduled",
      noNotes: "No notes available",
      stepsGoal: "10,000 steps / day",
      waterGoal: "6lts water / day",
      sleepGoal: "8 hours sleep / day",
      days: "Days",
      fitnessGuide: "Your Fitness Guide",
      dailyMissions: "Daily Missions",
      dailyMissionsSubtitle: "Stay consistent with your health goals.",
      bonusChallenges: "Bonus Challenges",
      todayHealthMetrics: "Today's Health Metrics",
      limited: "Limited",
      noDailyMissions: "No daily missions for today yet.",
      beginnerYoga: "Beginner Yoga Tutorial",
      yogaSubtitle: "Yoga Tutorial for beginners",
      taraGuide: "Your Tara Guide",
      taraTagline: "Lighting the Way to a Healthier Future",
      lowSpo2: "Low SpO₂",
      lowSpo2Desc: "Your SpO₂ level is lower than usual. Please avoid heavy lifting exercises.",
      message: "Message",
      messagePlaceholder: "Message...",
      taraTyping: "@tara is typing...",
      steps: "Steps",
      calories: "Calories",
      sleep: "Sleep",
      waterIntake: "Water Intake",
      bloodPressure: "Blood Pressure",
      heartRate: "Heart Rate",
      glucoseLevel: "Glucose Level",
      oxygenSpo2: "Oxygen SpO₂",
      hoursUnit: "hours",
      mlUnit: "ml",
      calUnit: "cal",
      stepsUnit: "steps",
      respRate: "Resp Rate",
      breathsPerMin: "breaths/min",
      hrv: "HRV",
      milliseconds: "ms",
      stress: "Stress",
      stressLevel: "level",
      goalTypes: {
        UNDERSTAND_MY_HEALTH: "Understand My Health",
        HEALTH_AWARENESS: "Health Awareness",
        MANAGE_A_CONDITION: "Manage a Condition",
        CONDITION_MANAGEMENT: "Condition Management",
        LIFE_STAGE_AND_FAMILY: "Life Stage & Family",
        MIND_STRESS_AND_SLEEP: "Mind, Stress & Sleep",
        MENTAL_WELLNESS: "Mental Wellness",
        GET_FIT_AND_MOVE_MORE: "Get Fit & Move More",
        FITNESS_AND_ACTIVITY: "Fitness And Activity",
        EAT_AND_NOURISH_BETTER: "Eat & Nourish Better",
        NUTRITION_AND_DIET: "Nutrition And Diet",
      },
      filterByGoal: "Filter by goal",
      allGoals: "All goals",
      clearGoals: "Clear",
      goalsSelected: "goals selected",
      filterByLanguage: "Language",
      allLanguages: "All languages",
      videoFilters: "Video filters",
      languageEnglish: "English",
      languageHindi: "Hindi",
      noVideosForFilters: "No videos match your current filters. Try another goal or language.",
      loadingVideos: "Loading your fitness videos…",
      pickVideo: "Pick a video to play",
      video: "video",
      videos: "videos",
      nowPlaying: "Playing",
      noHealthVideos: "No health videos available for your interests yet.",
      videoUnsupported: "Your browser does not support video playback.",
      previous: "Previous",
      next: "Next",
      attachFile: "Attach a file",
      tapToTalk: "Tap to Talk"
    },
    authModal: {
      betterMeTitle: "Better Me Starts Here",
      welcomeSubtitle: "Welcome! Sign up or log in to continue",
      progressTracking: "Progress Tracking",
      progressTrackingDesc: "Visual charts for your health vitals.",
      medicationAlerts: "Medication Alerts",
      medicationAlertsDesc: "Smart reminders, never miss a dose.",
      doctorAppointments: "Doctor Appointments",
      doctorAppointmentsDesc: "Book & manage sessions instantly.",
      ...EN_AUTH_MODAL_CREDENTIAL,
      secureAccess: "SECURE ENCRYPTED ACCESS",
      pleaseLoginToContinue: "Please Login To Continue",
    },
    familyPage: {
      title: "Family Members",
      myFamily: "My Family Members",
      searchPlaceholder: "Search By Name",
      name: "Name",
      patientId: "Patient ID",
      memberId: "Member ID",
      relation: "Relation",
      dob: "Date of Birth",
      gender: "Gender",
      bloodGroup: "Blood Group",
      phone: "Phone",
      status: "Status",
      action: "Action",
      pending: "Pending",
      active: "Active",
      inactive: "Inactive",
      questionnairePending: "Questionnaire Pending",
      total: "Total",
      noData: "No data",
      addMember: "Add Member",
      addFamilyMember: "Add Family Member",
    },
    medCloud: {
      title: "MedCloud",
      subtitle: "Your secure digital health record vault",
      records: "Records",
      types: "Types",
      storage: "Storage",
      upload: "Upload",
      justNow: "Just now",
      uploading: "Uploading...",
      uploadSuccess: "{name} uploaded successfully!",
      uploadFailed: "Upload failed. Please try again.",
      missingUserId: "User ID not found. Please log in again and retry.",
      invalidFileType: "Only medical images, PDFs, or medical videos are allowed.",
      invalidFileTypeWithWord:
        "Only medical images, PDFs, Word documents, or medical videos are allowed.",
      analyzingFile: "Analyzing {name}...",
      uploadAnalyzedSuccess: "{name} uploaded and analyzed successfully!",
      uploadHealthRecord: "Upload Health Record",
      chooseHowToAdd: "Choose how to add your document",
      analyzingDocument: "Analyzing document...",
      browseFiles: "Browse Files",
      browseFilesDesc: "PDF, Word, Images, Videos",
      takePhoto: "Take Photo",
      takePhotoDesc: "Capture document or prescription",
      cameraNotFound: "We can not find your camera",
      closeUploadDrawer: "Close upload drawer",
      remainingOf: "remaining of",
      deleteConfirmTitle: "Delete this record?",
      deleteSuccess: "File deleted successfully",
      deleteFailed: "Failed to delete file. Please try again.",
      emptyVault: "No health records yet. Upload your first file.",
      emptyFilter: "No records found in this category.",
      view: "View",
      viewDocument: "View {name}",
      download: "Download",
      downloading: "Downloading...",
      downloadSuccess: "Download started.",
      downloadFailed: "Download failed. Please try again.",
      viewFailed: "Unable to open this document. Please try again.",
      loadingDocument: "Loading document...",
      previewUnavailable: "Preview is not available for this file.",
      officePreviewHint: "This document type cannot be previewed in the browser. Use Download to save it.",
      closePreview: "Close",
      deleting: "Deleting...",
    },
    landing: {
      heroTitle: "Meet Tara: The Intelligent Core of Hospital Efficiency",
      heroDescription:
        "Revolutionize your healthcare facility with an AI-driven ecosystem designed to streamline workflows, enhance patient care, and eliminate administrative bottlenecks.",
      getStartedNow: "Get Started Now",
      learnMore: "Learn More",
      aboutTitle: "Empowering Every Hospital with a Unified Digital Core",
      faqTitle: "Frequently Asked Questions",
      offer1Title: "Analyze Medical Reports",
      offer1Text:
        "Automatically scan and interpret complex diagnostic data. Our AI identifies key health markers to provide a clear understanding of your medical standing instantly.",
      offer2Title: "AI Care Summaries",
      offer2Text:
        "Get concise, easy-to-read summaries of your doctor visits and lab results. Tara distills long medical documents into actionable health insights for better recovery.",
      offer3Title: "Manage Family Records",
      offer3Text:
        "Keep your entire family's health history organized in one secure portal. Access prescriptions, vaccination logs, and past reports for every family member with a single click.",
      hosting1Title: "Integrated Video Calls",
      hosting1Desc: "High-definition, secure video conferencing designed for clinical consultations.",
      hosting2Title: "Real-time Patient Chat",
      hosting2Desc: "Instant messaging to stay connected with patients and provide immediate guidance.",
      hosting3Title: "Digital Prescriptions",
      hosting3Desc: "Generate and share secure digital prescriptions instantly during or after consultations.",
      faq1Q: "How secure is myTara with sensitive hospital data?",
      faq1A:
        "Security is our top priority. myTara uses enterprise-grade end-to-end encryption and is fully compliant with healthcare data protection standards, ensuring that patient records and hospital data remain private and protected.",
      faq2Q: "Can Tara AI handle complex medical triage?",
      faq2A:
        "Yes, Tara is trained on extensive medical datasets to assist with symptom summarization and initial triage. However, it is designed to act as a support tool for healthcare professionals, not a replacement for clinical judgment.",
      faq3Q: "How long does it take to implement myTara in a hospital?",
      faq3A:
        "Our cloud-based architecture allows for rapid deployment. A standard hospital setup, including staff onboarding and data migration, typically takes between 7 to 14 days depending on the institution's size.",
      faq4Q: "Does the software support integration with existing lab equipment?",
      faq4A:
        "myTara is built with a flexible API-first approach, allowing seamless integration with most modern diagnostic equipment and laboratory information systems to centralize all your patient data in one dashboard.",
    },
    chatbot: {
      welcome: "Welcome! How can I help?",
      welcome_title: "Your Health Assistant",
      welcome_subtitle:
        "Ask about symptoms, medications, lab reports, or anything health-related.",
      headerSubtitle: "Chat with",
      headerTitle: "Online Health Assistant",
      newChat: "New Chat",
      sendMessage: "Tell me how you're feeling…",
      selectFamilyMember: "Who is this report for?",
      selectLanguage: "Choose a language to view the report analysis?",
      selectDocumentLanguage: "Choose a language to view the report analysis?",
      processingDocument: "Processing Document",
      failedToProcess: "Failed to process report. Please try again.",
      relatedTopics: "Related topics:",
      suggestedQuestions: "Suggestions:",
      quickVitalsLog: "Quick Vitals Log",
      logDailyHealth: "Log your daily health metrics",
      quickHealthActions: "Quick Health Actions",
      healthToolsSubtitle: "Access your health tools instantly",
      actionVitalReports: "Vital Reports",
      actionVitalReportsDesc: "Heart rate & BP",
      actionMedication: "Medication",
      actionMedicationDesc: "Prescriptions",
      actionReports: "Reports",
      actionReportsDesc: "Medical records",
      actionHealthSummary: "Health Summary",
      actionHealthSummaryDesc: "Wellness overview",
      actionFamilyMember: "Family Member",
      actionFamilyMemberDesc: "Manage profiles",
      actionAppointments: "Appointments",
      actionAppointmentsDesc: "Upcoming visits",
      vitalHeartRate: "Heart Rate",
      vitalBloodPressure: "Blood Pressure",
      vitalTemperature: "Temperature",
      vitalMedicine: "Medicine",
      quickPicks: "Quick picks",
      workingOnResponse: "Working on a response... Please wait.",
      queryFamily: "Show my family members",
      queryMedication: "Show my medications",
      queryVitals: "Show my vital reports",
      queryAppointments: "Show my appointments",
      queryReports: "Show my reports",
      querySummary: "Show my health summary",
      loggedVitals: "Logged {title}: {value}",
    },
    login: {
      welcomeBack: "Welcome Back",
      signInContinue: "Sign in to continue to myTara.",
      continueGoogle: "Continue With Google",
      continueEmail: "Continue With Email",
      continuePhone: "Continue With Phone Number",
      or: "OR",
      emailOrMemberId: "Email or Member ID*",
      ...EN_LOGIN_CREDENTIAL,
      rememberMe: "Remember Me",
      loginLoading: "LOGIN...",
      signIn: "SIGN IN",
      noAccount: "Don't have an account?",
      createAccount: "Create account",
      dataSecure: "Your data is secure & encrypted",
    },
    authContact: {
      verifyEmailAddress: "Verify Email Address",
      verifyPhoneNumber: "Verify Phone Number",
      enterDetailsForOtp: "Enter your details to get the OTP",
      enterMobileForOtp: "Enter your mobile number to get the OTP",
      emailPlaceholder: "you@gmail.com",
      fullName: "Full name",
      enter10DigitNumber: "Enter 10 digit number",
      emailConfirmHint: "We'll send you a code to confirm your email address.",
      phoneConfirmHint: "We'll send you a code to confirm your phone number.",
      serviceMessagesHint: "We may occasionally send you service-based messages.",
      next: "Next",
      send: "Send",
      enterSecurityCode: "Enter Security Code",
      codeSentTo: "Code sent to",
      didntReceiveCode: "Didn't receive the code?",
      resendIn: "Resend in",
      resendCode: "Resend code",
      resendNow: "Resend Now",
      clear: "Clear",
      close: "Close",
      invalidEmailFormat: "Please enter a valid email address (e.g., name@domain.com)",
    },
    register: {
      title: "Register a New Membership",
      disclaimer:
        "This tool is for educational purposes only. Our AI is not a doctor and cannot provide a medical diagnosis or treatment. Results are based only on the information you share, which may be incomplete. Please consult a licensed healthcare professional for medical advice or treatment.",
      alreadyHaveAccount: "Already have an account?",
      signIn: "Sign In",
    },
    taraReports: {
      pageTitle: "Reports",
      myTaraReports: "My Tara Reports",
      aiChatSummary: "AI Chat Summary",
      healthCareSummary: "Health Care Summary",
      searchPlaceholder: "Search...",
      month: "Month",
      year: "Year",
      loadingSummaries: "Loading summaries...",
      failedToLoad: "Failed to load summaries.",
      noData: "No data",
      totalReports: "Total {count} Reports",
      dateTime: "Date & Time",
      aiSolutionSummary: "AI Solution Summary",
      problemStatement: "Problem Statement",
      chatLogs: "Chat Logs",
    },
    sidebar: {
      dashboard: "Dashboard",
      familySearch: "Family Search",
      calendar: "Calendar",
      reports: "Reports",
      medicalReports: "Medical Reports",
      myTaraReports: "MyTara Reports",
      vitalsReports: "Vitals Reports",
      medication: "Medication",
      medCloud: "MedCloud",
      settings: "Settings",
      files: "Files",
      allRightsReserved: "All Rights Reserved",
    },
    subheader: {
      rewardsProgress: "Rewards & progress",
      rewardsSubtitle: "Every login adds +1 coin · Complete tasks to earn more",
      balance: "Balance",
      redeem: "Redeem",
      openCoinWallet: "Open Rewards & Coins wallet",
    },
    dashboard: {
      welcomeBack: "Welcome Back,",
      healthStatsToday: "Here's what's happening with your health stats today.",
      heartRate: "Heart Rate",
      glucose: "Glucose Level(Sugar)",
      oxygen: "Oxygen",
      noData: "No data available",
      weeklyMedicineSchedule: "Weekly Medicine Schedule",
      markAsTaken: "Mark as Taken",
      taken: "Taken",
      futureMedication: "Medication is scheduled for a future time.",
      details: "Details",
      noMedicines: "No medicines scheduled",
      notifications: "Notifications",
      noNotifications: "No notifications",
      markMedicationTitle: "Mark Medication as Taken?",
      confirmMedicationIntake: "Confirm Medication Intake",
      medication: "Medication",
      time: "Time",
      helperText: "Are you sure you want to mark this medication as taken? This will update your health record.",
      yesMarkTaken: "Yes, Mark as Taken",
      cancel: "Cancel",
    },
  },
  hi: {
    common: {
      language: "भाषा",
      selectLanguage: "भाषा चुनें",
      login: "लॉगिन",
      registerNow: "अभी रजिस्टर करें",
      profile: "प्रोफ़ाइल",
      ...buildChangeCredentialLabel("पासवर्ड बदलें"),
      logout: "लॉगआउट",
      retry: "पुनः प्रयास",
      notifications: "सूचनाएं",
      self: "स्वयं",
      familyMember: "परिवार सदस्य",
      applyingLanguage: "भाषा अपडेट हो रही है…",
      loading: "लोड हो रहा है…",
      back: "पीछे",
      update: "अपडेट",
      cancel: "रद्द करें",
      delete: "हटाएं",
      healthInterests: "स्वास्थ्य रुचियाँ",
      selectedMember: "चयनित सदस्य",
    },
    healthInterests: {
      title: "अपनी स्वास्थ्य रुचियाँ चुनें",
      subtitle:
        "जो आपके लिए ज़रूरी है उसे चुनें — प्रोफाइल मेनू से कभी भी बदल सकते हैं।",
      continue: "जारी रखें",
      save: "रुचियाँ सहेजें",
      close: "बंद करें",
      pickAtLeastOne: "जारी रखने के लिए कम से कम एक रुचि चुनें।",
      infoHint: "किसी रुचि पर होवर या टैप करें — जानकारी यहाँ दिखेगी।",
      dailyExerciseInfo:
        "वर्कआउट योजना, गतिविधि रिमाइंडर और रोज़ सक्रिय रहने के टिप्स।",
      peaceOfMindInfo:
        "ध्यान, श्वास अभ्यास और तनाव कम करने के लिए मार्गदर्शन।",
      healthyEatingInfo:
        "भोजन विचार, पोषण टिप्स और संतुलित आहार ट्रैकिंग।",
      doctorCheckupsInfo:
        "डॉक्टर विज़िट बुक करें, फॉलो-अप रिमाइंडर और जांच शेड्यूल।",
      medicineRemindersInfo:
        "दवा की खुराक कभी न चूकें — अलर्ट और शेड्यूल।",
      vitalsTrackingInfo:
        "ब्लड प्रेशर, हृदय गति, ग्लूकोज़ आदि एक जगह लॉग करें।",
      aiHealthAnalysisInfo:
        "AI स्वास्थ्य स्कैन और रिपोर्ट/लक्षणों से जानकारी।",
      liveHealthInsightsInfo:
        "आपके नवीनतम डेटा पर रियल-टाइम ट्रेंड और अलर्ट।",
      healthImprovementInfo:
        "वेलनेस लक्ष्य सेट करें और प्रगति ट्रैक करें।",
      smartReportInfo:
        "लैब रिपोर्ट और रिकॉर्ड की आसान स्मार्ट सारांश।",
      saved: "आपकी स्वास्थ्य रुचियाँ अपडेट हो गईं।",
      savedWelcome: "बढ़िया! हम आपका अनुभव व्यक्तिगत बनाएंगे।",
      loginRequired: "कृपया दोबारा साइन इन करें।",
      loadFailed:
        "स्वास्थ्य रुचियाँ लोड नहीं हो सकीं। कृपया बंद करके दोबारा कोशिश करें।",
      saveFailed:
        "स्वास्थ्य रुचियाँ सेव नहीं हो सकीं। कृपया दोबारा कोशिश करें।",
      saving: "सेव हो रहा है...",
      dailyExercise: "दैनिक व्यायाम",
      peaceOfMind: "मन की शांति",
      healthyEating: "स्वस्थ आहार",
      doctorCheckups: "डॉक्टर जांच",
      medicineReminders: "दवा अलर्ट",
      vitalsTracking: "वाइटल ट्रैक",
      aiHealthAnalysis: "AI स्वास्थ्य स्कैन",
      liveHealthInsights: "लाइव जानकारी",
      healthImprovement: "वेलनेस लक्ष्य",
      smartReport: "स्मार्ट रिपोर्ट",
    },
    questionnaire: {
      title: "प्रारंभिक स्वास्थ्य प्रश्नावली",
      basicInfo: "मूल जानकारी",
      emergency: "आपातकाल",
      healthOverview: "स्वास्थ्य अवलोकन",
      medications: "दवाएं",
      familyHistory: "पारिवारिक इतिहास",
      goals: "लक्ष्य",
      finalInput: "अंतिम इनपुट",
    },
    aiDashboard: {
      goodMorning: "सुप्रभात",
      goodAfternoon: "शुभ दोपहर",
      goodEvening: "शुभ संध्या",
      takeCharge: "आज अपने स्वास्थ्य का नियंत्रण लें!",
      healthStatsToday: "आज आपके स्वास्थ्य आंकड़ों में क्या हो रहा है।",
      secureRecords: "अपने स्वास्थ्य रिकॉर्ड सुरक्षित करें",
      checkVitals: "अपने वाइटल संकेत जांचें",
      uploadVital: "वाइटल अपलोड करें",
      vitalsReports: "वाइटल्स रिपोर्ट्स",
      uploadReports: "रिपोर्ट अपलोड करें",
      taraUploadHint: "विश्लेषण देखने के लिए डैशबोर्ड से रिपोर्ट अपलोड करें या यहाँ फ़ाइल संलग्न करें।",
      analysisPleaseWait: "विश्लेषण हो रहा है, कृपया प्रतीक्षा करें…",
      analyzingStatus: "विश्लेषण हो रहा है",
      analysisHint: "हम आपकी रिपोर्ट पढ़ रहे हैं। इसमें कुछ समय लग सकता है।",
      duplicateReportNotSaved: "यह दस्तावेज़ पहले से मेड क्लाउड में है। डुप्लिकेट फ़ाइल सेव नहीं हुई।",
      reportSavedToMedCloud: "{name} मेड क्लाउड में सेव हो गई।",
      reportMedCloudSaveFailed: "रिपोर्ट का विश्लेषण हुआ लेकिन मेड क्लाउड में सेव नहीं हो सकी।",
      reportAnalyzeFailed: "रिपोर्ट का विश्लेषण विफल। कृपया पुनः प्रयास करें।",
      reportUploadLoginRequired: "रिपोर्ट अपलोड करने के लिए कृपया दोबारा लॉग इन करें।",
      openMedCloud: "मेड क्लाउड खोलें",
      today: "आज",
      average: "औसत",
      medicineTracker: "दवा ट्रैकर",
      allTasks: "सभी कार्य",
      noMedicines: "कोई दवा उपलब्ध नहीं",
      upcoming: "आगामी",
      taken: "ले लिया",
      missed: "छूट गया",
      markTaken: "लिया चिह्नित करें",
      details: "विवरण",
      scheduled: "निर्धारित",
      noNotes: "कोई नोट उपलब्ध नहीं",
      stepsGoal: "10,000 कदम / दिन",
      waterGoal: "6 लीटर पानी / दिन",
      sleepGoal: "8 घंटे नींद / दिन",
      days: "दिन",
      fitnessGuide: "आपका फिटनेस गाइड",
      dailyMissions: "दैनिक मिशन",
      dailyMissionsSubtitle: "अपने स्वास्थ्य लक्ष्यों पर बने रहें।",
      bonusChallenges: "बोनस चुनौतियाँ",
      todayHealthMetrics: "आज के स्वास्थ्य मेट्रिक्स",
      limited: "सीमित",
      noDailyMissions: "आज के लिए कोई दैनिक मिशन नहीं है।",
      beginnerYoga: "शुरुआती योग ट्यूटोरियल",
      yogaSubtitle: "शुरुआती लोगों के लिए योग",
      taraGuide: "आपका तारा गाइड",
      taraTagline: "स्वस्थ भविष्य की ओर रास्ता",
      lowSpo2: "कम SpO₂",
      lowSpo2Desc: "आपका SpO₂ स्तर सामान्य से कम है। भारी वजन उठाने से बचें।",
      message: "संदेश",
      messagePlaceholder: "संदेश...",
      taraTyping: "@tara टाइप कर रहा है...",
      steps: "कदम",
      calories: "कैलोरी",
      sleep: "नींद",
      waterIntake: "पानी का सेवन",
      bloodPressure: "रक्तचाप",
      heartRate: "हृदय गति",
      glucoseLevel: "ग्लूकोज़ स्तर",
      oxygenSpo2: "ऑक्सीजन SpO₂",
      hoursUnit: "घंटे",
      mlUnit: "मि.ली.",
      calUnit: "कैल",
      stepsUnit: "कदम",
      respRate: "श्वसन दर",
      breathsPerMin: "साँस/मिनट",
      hrv: "हृदय गति विभिन्नता",
      milliseconds: "मि.से.",
      stress: "तनाव",
      stressLevel: "स्तर",
      goalTypes: {
        UNDERSTAND_MY_HEALTH: "अपने स्वास्थ्य को समझें",
        HEALTH_AWARENESS: "स्वास्थ्य जागरूकता",
        MANAGE_A_CONDITION: "स्थिति का प्रबंधन",
        CONDITION_MANAGEMENT: "स्थिति प्रबंधन",
        LIFE_STAGE_AND_FAMILY: "जीवन के चरण और परिवार",
        MIND_STRESS_AND_SLEEP: "मन, तनाव और नींद",
        MENTAL_WELLNESS: "मानसिक स्वास्थ्य",
        GET_FIT_AND_MOVE_MORE: "फिट रहें और अधिक हिलें",
        FITNESS_AND_ACTIVITY: "फिटनेस और गतिविधि",
        EAT_AND_NOURISH_BETTER: "बेहतर खाएँ और पोषण लें",
        NUTRITION_AND_DIET: "पोषण और आहार",
      },
      filterByGoal: "लक्ष्य के अनुसार फ़िल्टर करें",
      allGoals: "सभी लक्ष्य",
      clearGoals: "साफ़ करें",
      goalsSelected: "लक्ष्य चुने गए",
      filterByLanguage: "भाषा",
      allLanguages: "सभी भाषाएँ",
      videoFilters: "वीडियो फ़िल्टर",
      languageEnglish: "अंग्रेज़ी",
      languageHindi: "हिंदी",
      noVideosForFilters: "आपके वर्तमान फ़िल्टर से कोई वीडियो मेल नहीं खाता। कोई अन्य लक्ष्य या भाषा आज़माएँ।",
      loadingVideos: "आपके फिटनेस वीडियो लोड हो रहे हैं…",
      pickVideo: "चलाने के लिए वीडियो चुनें",
      video: "वीडियो",
      videos: "वीडियो",
      nowPlaying: "चल रहा है",
      noHealthVideos: "आपकी रुचियों के लिए अभी कोई स्वास्थ्य वीडियो उपलब्ध नहीं है।",
      videoUnsupported: "आपका ब्राउज़र वीडियो प्लेबैक का समर्थन नहीं करता।",
      previous: "पिछला",
      next: "अगला",
      attachFile: "फ़ाइल अटैच करें",
      tapToTalk: "बोलने के लिए टैप करें"
    },
    authModal: {
      betterMeTitle: "बेहतर स्वास्थ्य यहीं से शुरू",
      welcomeSubtitle: "स्वागत है! जारी रखने के लिए साइन अप या लॉग इन करें",
      progressTracking: "प्रगति ट्रैकिंग",
      progressTrackingDesc: "आपके स्वास्थ्य वाइटल्स के लिए विज़ुअल चार्ट।",
      medicationAlerts: "दवा अलर्ट",
      medicationAlertsDesc: "स्मार्ट रिमाइंडर, कोई खुराक न छूटे।",
      doctorAppointments: "डॉक्टर अपॉइंटमेंट",
      doctorAppointmentsDesc: "तुरंत सेशन बुक और प्रबंधित करें।",
      ...buildSwitchCredentialLoginLabel("पासवर्ड लॉगिन पर जाएं"),
      secureAccess: "सुरक्षित एन्क्रिप्टेड एक्सेस",
      pleaseLoginToContinue: "जारी रखने के लिए कृपया लॉगिन करें",
    },
    familyPage: {
      title: "परिवार सदस्य",
      myFamily: "मेरे परिवार सदस्य",
      searchPlaceholder: "नाम से खोजें",
      name: "नाम",
      patientId: "मरीज़ ID",
      memberId: "सदस्य ID",
      relation: "संबंध",
      dob: "जन्म तिथि",
      gender: "लिंग",
      bloodGroup: "रक्त समूह",
      phone: "फ़ोन",
      status: "स्थिति",
      action: "कार्रवाई",
      pending: "लंबित",
      active: "सक्रिय",
      inactive: "निष्क्रिय",
      questionnairePending: "प्रश्नावली लंबित",
      total: "कुल",
      noData: "कोई डेटा नहीं",
      addMember: "सदस्य जोड़ें",
      addFamilyMember: "परिवार सदस्य जोड़ें",
    },
    medCloud: {
      title: "मेडक्लाउड",
      subtitle: "आपका सुरक्षित डिजिटल स्वास्थ्य रिकॉर्ड वॉल्ट",
      records: "रिकॉर्ड",
      types: "प्रकार",
      storage: "स्टोरेज",
      upload: "अपलोड",
      justNow: "अभी",
      uploading: "अपलोड हो रहा है...",
      uploadSuccess: "{name} सफलतापूर्वक अपलोड हो गया!",
      uploadFailed: "अपलोड विफल। कृपया पुनः प्रयास करें।",
      missingUserId: "यूज़र ID नहीं मिली। कृपया दोबारा लॉगिन करें।",
      invalidFileType: "केवल मेडिकल छवि, PDF या मेडिकल वीडियो की अनुमति है।",
      invalidFileTypeWithWord:
        "केवल मेडिकल छवि, PDF, Word दस्तावेज़ या मेडिकल वीडियो की अनुमति है।",
      analyzingFile: "{name} का विश्लेषण हो रहा है...",
      uploadAnalyzedSuccess: "{name} सफलतापूर्वक अपलोड और विश्लेषित हो गया!",
      uploadHealthRecord: "स्वास्थ्य रिकॉर्ड अपलोड करें",
      chooseHowToAdd: "दस्तावेज़ जोड़ने का तरीका चुनें",
      analyzingDocument: "दस्तावेज़ विश्लेषण हो रहा है...",
      browseFiles: "फ़ाइल ब्राउज़ करें",
      browseFilesDesc: "PDF, Word, Images, Videos",
      takePhoto: "फ़ोटो लें",
      takePhotoDesc: "दस्तावेज़ या पर्ची की फ़ोटो लें",
      cameraNotFound: "कैमरा नहीं मिला",
      closeUploadDrawer: "अपलोड ड्रॉअर बंद करें",
      remainingOf: "में से शेष",
      deleteConfirmTitle: "यह रिकॉर्ड हटाएं?",
      deleteSuccess: "फ़ाइल सफलतापूर्वक हटाई गई",
      deleteFailed: "फ़ाइल हटाने में विफल। कृपया पुनः प्रयास करें।",
      emptyVault: "अभी कोई स्वास्थ्य रिकॉर्ड नहीं। अपनी पहली फ़ाइल अपलोड करें।",
      emptyFilter: "इस श्रेणी में कोई रिकॉर्ड नहीं मिला।",
      view: "देखें",
      viewDocument: "{name} देखें",
      download: "डाउनलोड",
      downloading: "डाउनलोड हो रहा है...",
      downloadSuccess: "डाउनलोड शुरू हो गया।",
      downloadFailed: "डाउनलोड विफल। कृपया पुनः प्रयास करें।",
      viewFailed: "यह दस्तावेज़ खोला नहीं जा सका। कृपया पुनः प्रयास करें।",
      loadingDocument: "दस्तावेज़ लोड हो रहा है...",
      previewUnavailable: "इस फ़ाइल के लिए पूर्वावलोकन उपलब्ध नहीं है।",
      officePreviewHint: "इस प्रकार के दस्तावेज़ का ब्राउज़र में पूर्वावलोकन नहीं हो सकता। सहेजने के लिए डाउनलोड करें।",
      closePreview: "बंद करें",
      deleting: "हटाया जा रहा है...",
    },
    landing: {
      heroTitle: "मिलिए तारा से: अस्पताल दक्षता का बुद्धिमान केंद्र",
      heroDescription:
        "एआई संचालित इकोसिस्टम के साथ अपने हेल्थकेयर संस्थान को बेहतर बनाएं, वर्कफ़्लो सरल करें और मरीज देखभाल को उन्नत करें।",
      getStartedNow: "अभी शुरू करें",
      learnMore: "और जानें",
      aboutTitle: "हर अस्पताल को एकीकृत डिजिटल केंद्र से सशक्त बनाना",
      faqTitle: "अक्सर पूछे जाने वाले प्रश्न",
      offer1Title: "मेडिकल रिपोर्ट विश्लेषण",
      offer1Text:
        "जटिल डायग्नोस्टिक डेटा को स्वचालित रूप से स्कैन और व्याख्या करें। हमारी AI मुख्य स्वास्थ्य मार्कर पहचानती है।",
      offer2Title: "AI केयर सारांश",
      offer2Text:
        "डॉक्टर विज़िट और लैब परिणामों के संक्षिप्त सारांश पाएं। तारा लंबे मेडिकल दस्तावेज़ों को सरल अंतर्दृष्टि में बदलती है।",
      offer3Title: "परिवार रिकॉर्ड प्रबंधन",
      offer3Text:
        "पूरे परिवार का स्वास्थ्य इतिहास एक सुरक्षित पोर्टल में रखें। प्रिस्क्रिप्शन, टीकाकरण और रिपोर्ट एक क्लिक में।",
      hosting1Title: "एकीकृत वीडियो कॉल",
      hosting1Desc: "क्लिनिकल परामर्श के लिए सुरक्षित HD वीडियो कॉन्फ्रेंसिंग।",
      hosting2Title: "रीयल-टाइम मरीज चैट",
      hosting2Desc: "मरीजों से जुड़े रहें और तुरंत मार्गदर्शन दें।",
      hosting3Title: "डिजिटल प्रिस्क्रिप्शन",
      hosting3Desc: "परामर्श के दौरान या बाद में सुरक्षित डिजिटल प्रिस्क्रिप्शन साझा करें।",
      faq1Q: "संवेदनशील अस्पताल डेटा के साथ myTara कितना सुरक्षित है?",
      faq1A:
        "सुरक्षा हमारी प्राथमिकता है। myTara एंटरप्राइज़-ग्रेड एन्क्रिप्शन और स्वास्थ्य डेटा मानकों का पालन करता है।",
      faq2Q: "क्या तारा AI जटिल मेडिकल ट्राइएज कर सकती है?",
      faq2A:
        "हाँ, तारा लक्षण सारांश और प्रारंभिक ट्राइएज में मदद करती है। यह डॉक्टरों का सहायक उपकरण है, प्रतिस्थापन नहीं।",
      faq3Q: "अस्पताल में myTara लागू करने में कितना समय लगता है?",
      faq3A:
        "क्लाउड आर्किटेक्चर से तेज़ तैनाती। आमतौर पर 7–14 दिन, संस्थान के आकार पर निर्भर।",
      faq4Q: "क्या यह मौजूदा लैब उपकरणों के साथ जुड़ सकता है?",
      faq4A:
        "myTara लचीले API के साथ आधुनिक डायग्नोस्टिक और लैब सिस्टम से जुड़ता है।",
    },
    chatbot: {
      welcome: "स्वागत है! मैं कैसे मदद करूं?",
      welcome_title: "आपका स्वास्थ्य सहायक",
      welcome_subtitle:
        "लक्षण, दवाइयाँ, लैब रिपोर्ट या स्वास्थ्य से जुड़ी किसी भी बात के बारे में पूछें।",
      headerSubtitle: "इनसे चैट करें",
      headerTitle: "ऑनलाइन स्वास्थ्य सहायक",
      newChat: "नई चैट",
      sendMessage: "बताइए आप कैसा महसूस कर रहे हैं…",
      selectFamilyMember: "यह रिपोर्ट किसके लिए है?",
      selectLanguage: "रिपोर्ट विश्लेषण देखने के लिए भाषा चुनें?",
      selectDocumentLanguage: "रिपोर्ट विश्लेषण देखने के लिए भाषा चुनें?",
      processingDocument: "दस्तावेज़ प्रोसेस हो रहा है",
      failedToProcess: "रिपोर्ट प्रोसेस नहीं हो पाई। कृपया पुनः प्रयास करें।",
      relatedTopics: "संबंधित विषय:",
      suggestedQuestions: "सुझाए गए प्रश्न:",
      quickVitalsLog: "क्विक वाइटल्स लॉग",
      logDailyHealth: "अपनी दैनिक स्वास्थ्य जानकारी दर्ज करें",
      quickHealthActions: "त्वरित स्वास्थ्य कार्य",
      healthToolsSubtitle: "अपने स्वास्थ्य उपकरण तुरंत एक्सेस करें",
      actionVitalReports: "वाइटल रिपोर्ट्स",
      actionVitalReportsDesc: "हृदय गति और बीपी",
      actionMedication: "दवा",
      actionMedicationDesc: "पर्चे",
      actionReports: "रिपोर्ट्स",
      actionReportsDesc: "मेडिकल रिकॉर्ड",
      actionHealthSummary: "स्वास्थ्य सारांश",
      actionHealthSummaryDesc: "वेलनेस अवलोकन",
      actionFamilyMember: "परिवार सदस्य",
      actionFamilyMemberDesc: "प्रोफाइल प्रबंधित करें",
      actionAppointments: "अपॉइंटमेंट",
      actionAppointmentsDesc: "आगामी विजिट",
      vitalHeartRate: "हृदय गति",
      vitalBloodPressure: "रक्तचाप",
      vitalTemperature: "तापमान",
      vitalMedicine: "दवा",
      quickPicks: "त्वरित चयन",
      queryFamily: "मेरे परिवार के सदस्य दिखाएं",
      queryMedication: "मेरी दवाएं दिखाएं",
      queryVitals: "मेरी वाइटल रिपोर्ट्स दिखाएं",
      queryAppointments: "मेरे अपॉइंटमेंट दिखाएं",
      queryReports: "मेरी रिपोर्ट्स दिखाएं",
      querySummary: "मेरा स्वास्थ्य सारांश दिखाएं",
      loggedVitals: "लॉग किया गया {title}: {value}",
    },
    login: {
      welcomeBack: "वापसी पर स्वागत है",
      signInContinue: "myTara जारी रखने के लिए साइन इन करें।",
      continueGoogle: "Google से जारी रखें",
      continueEmail: "ईमेल से जारी रखें",
      continuePhone: "फोन नंबर से जारी रखें",
      or: "या",
      emailOrMemberId: "ईमेल या सदस्य आईडी*",
      ...buildLoginCredentialLabels({
        field: "पासवर्ड*",
        reset: "पासवर्ड रीसेट करें?",
      }),
      rememberMe: "मुझे याद रखें",
      loginLoading: "लॉगिन...",
      signIn: "साइन इन",
      noAccount: "क्या आपका अकाउंट नहीं है?",
      createAccount: "अकाउंट बनाएं",
      dataSecure: "आपका डेटा सुरक्षित और एन्क्रिप्टेड है",
    },
    authContact: {
      verifyEmailAddress: "ईमेल पता सत्यापित करें",
      verifyPhoneNumber: "फ़ोन नंबर सत्यापित करें",
      enterDetailsForOtp: "OTP पाने के लिए अपना विवरण दर्ज करें",
      enterMobileForOtp: "OTP पाने के लिए अपना मोबाइल नंबर दर्ज करें",
      emailPlaceholder: "you@gmail.com",
      fullName: "पूरा नाम",
      enter10DigitNumber: "10 अंकों का नंबर दर्ज करें",
      emailConfirmHint: "हम आपके ईमेल पते की पुष्टि के लिए एक कोड भेजेंगे।",
      phoneConfirmHint: "हम आपके फ़ोन नंबर की पुष्टि के लिए एक कोड भेजेंगे।",
      serviceMessagesHint: "हम समय-समय पर सेवा संबंधी संदेश भेज सकते हैं।",
      next: "आगे",
      send: "भेजें",
      enterSecurityCode: "सुरक्षा कोड दर्ज करें",
      codeSentTo: "कोड भेजा गया",
      didntReceiveCode: "कोड नहीं मिला?",
      resendIn: "पुनः भेजें",
      resendCode: "कोड पुनः भेजें",
      resendNow: "अभी पुनः भेजें",
      clear: "साफ़ करें",
      close: "बंद करें",
      invalidEmailFormat: "कृपया मान्य ईमेल पता दर्ज करें (उदा., name@domain.com)",
    },
    register: {
      title: "नई सदस्यता पंजीकरण",
      disclaimer:
        "यह उपकरण केवल शैक्षिक उद्देश्यों के लिए है। हमारी AI डॉक्टर नहीं है और चिकित्सा निदान या उपचार नहीं दे सकती। परिणाम केवल आपके द्वारा साझा की गई जानकारी पर आधारित हैं, जो अधूरी हो सकती है। चिकित्सा सलाह या उपचार के लिए कृपया लाइसेंस प्राप्त स्वास्थ्य पेशेवर से परामर्श करें।",
      alreadyHaveAccount: "पहले से खाता है?",
      signIn: "साइन इन",
    },
    taraReports: {
      pageTitle: "रिपोर्ट्स",
      myTaraReports: "माईतारा रिपोर्ट्स",
      aiChatSummary: "AI चैट सारांश",
      healthCareSummary: "स्वास्थ्य सारांश",
      searchPlaceholder: "खोजें...",
      month: "महीना",
      year: "वर्ष",
      loadingSummaries: "सारांश लोड हो रहे हैं...",
      failedToLoad: "सारांश लोड करने में विफल।",
      noData: "कोई डेटा नहीं",
      totalReports: "कुल {count} रिपोर्ट्स",
      dateTime: "दिनांक और समय",
      aiSolutionSummary: "AI समाधान सारांश",
      problemStatement: "समस्या विवरण",
      chatLogs: "चैट लॉग",
    },
    sidebar: {
      dashboard: "डैशबोर्ड",
      familySearch: "परिवार खोज",
      calendar: "कैलेंडर",
      reports: "रिपोर्ट्स",
      medicalReports: "मेडिकल रिपोर्ट्स",
      myTaraReports: "माईतारा रिपोर्ट्स",
      vitalsReports: "वाइटल्स रिपोर्ट्स",
      medication: "दवा",
      medCloud: "मेडक्लाउड",
      files: "फ़ाइलें",
      allRightsReserved: "सर्वाधिकार सुरक्षित",
    },
    subheader: {
      rewardsProgress: "रिवार्ड्स और प्रगति",
      rewardsSubtitle: "हर लॉगिन पर +1 कॉइन मिलता है · कार्य पूरे करें और अधिक कमाएं",
      balance: "बैलेंस",
      redeem: "रिडीम",
      openCoinWallet: "रिवार्ड्स और कॉइन वॉलेट खोलें",
    },
    dashboard: {
      welcomeBack: "वापसी पर स्वागत है,",
      healthStatsToday: "आज आपके स्वास्थ्य आंकड़ों में क्या हो रहा है, यहां देखें।",
      heartRate: "हृदय गति",
      glucose: "ग्लूकोज़ स्तर(शुगर)",
      oxygen: "ऑक्सीजन",
      noData: "डेटा उपलब्ध नहीं है",
      weeklyMedicineSchedule: "साप्ताहिक दवा शेड्यूल",
      markAsTaken: "लिए गए के रूप में चिह्नित करें",
      taken: "ले लिया गया",
      futureMedication: "दवा भविष्य के समय के लिए निर्धारित है।",
      details: "विवरण",
      noMedicines: "कोई दवा निर्धारित नहीं है",
      notifications: "सूचनाएं",
      noNotifications: "कोई सूचना नहीं",
      markMedicationTitle: "दवा को लिया हुआ चिह्नित करें?",
      confirmMedicationIntake: "दवा सेवन की पुष्टि करें",
      medication: "दवा",
      time: "समय",
      helperText: "क्या आप इस दवा को लिया हुआ चिह्नित करना चाहते हैं? इससे आपका स्वास्थ्य रिकॉर्ड अपडेट होगा।",
      yesMarkTaken: "हां, लिया हुआ चिह्नित करें",
      cancel: "रद्द करें",
    },
  },
  pa: {
    common: {
      language: "ਭਾਸ਼ਾ",
      selectLanguage: "ਭਾਸ਼ਾ ਚੁਣੋ",
      login: "ਲਾਗਇਨ",
      registerNow: "ਹੁਣੇ ਰਜਿਸਟਰ ਕਰੋ",
      profile: "ਪ੍ਰੋਫਾਇਲ",
      ...buildChangeCredentialLabel("ਪਾਸਵਰਡ ਬਦਲੋ"),
      logout: "ਲਾਗਆਉਟ",
      retry: "ਮੁੜ ਕੋਸ਼ਿਸ਼ ਕਰੋ",
      notifications: "ਸੂਚਨਾਵਾਂ",
      self: "ਆਪਣਾ",
      familyMember: "ਪਰਿਵਾਰ ਮੈਂਬਰ",
      applyingLanguage: "ਭਾਸ਼ਾ ਅਪਡੇਟ ਹੋ ਰਹੀ ਹੈ…",
      loading: "ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ…",
    },
    landing: {
      heroTitle: "ਤਾਰਾ ਨਾਲ ਮਿਲੋ: ਹਸਪਤਾਲ ਕੁਸ਼ਲਤਾ ਦਾ ਸਮਾਰਟ ਕੇਂਦਰ",
      heroDescription:
        "AI ਅਧਾਰਿਤ ਇਕੋਸਿਸਟਮ ਨਾਲ ਹਸਪਤਾਲ ਵਰਕਫਲੋ ਤੇ ਮਰੀਜ਼ ਸੰਭਾਲ ਨੂੰ ਹੋਰ ਬਿਹਤਰ ਬਣਾਓ।",
      getStartedNow: "ਹੁਣੇ ਸ਼ੁਰੂ ਕਰੋ",
      learnMore: "ਹੋਰ ਜਾਣੋ",
      aboutTitle: "ਹਰ ਹਸਪਤਾਲ ਨੂੰ ਇਕਜੁਟ ਡਿਜ਼ਿਟਲ ਕੋਰ ਨਾਲ ਮਜ਼ਬੂਤ ਬਣਾਉਣਾ",
      faqTitle: "ਅਕਸਰ ਪੁੱਛੇ ਜਾਣ ਵਾਲੇ ਸਵਾਲ",
    },
    chatbot: {
      welcome: "ਜੀ ਆਇਆਂ ਨੂੰ! ਮੈਂ ਕਿਵੇਂ ਮਦਦ ਕਰਾਂ?",
      welcome_title: "ਤੁਹਾਡਾ ਸਿਹਤ ਸਹਾਇਕ",
      welcome_subtitle:
        "ਲੱਛਣ, ਦਵਾਈਆਂ, ਲੈਬ ਰਿਪੋਰਟਾਂ ਜਾਂ ਸਿਹਤ ਨਾਲ ਸੰਬੰਧਿਤ ਕਿਸੇ ਵੀ ਗੱਲ ਬਾਰੇ ਪੁੱਛੋ।",
      headerSubtitle: "ਇਨ੍ਹਾਂ ਨਾਲ ਚੈਟ ਕਰੋ",
      headerTitle: "ਔਨਲਾਈਨ ਸਿਹਤ ਸਹਾਇਕ",
      newChat: "ਨਵੀਂ ਚੈਟ",
      sendMessage: "ਦੱਸੋ ਤੁਸੀਂ ਕਿਵੇਂ ਮਹਿਸੂਸ ਕਰ ਰਹੇ ਹੋ…",
      selectFamilyMember: "ਇਹ ਰਿਪੋਰਟ ਕਿਸ ਲਈ ਹੈ?",
      selectLanguage: "ਰਿਪੋਰਟ ਵਿਸ਼ਲੇਸ਼ਣ ਦੇਖਣ ਲਈ ਭਾਸ਼ਾ ਚੁਣੋ?",
      selectDocumentLanguage: "ਰਿਪੋਰਟ ਵਿਸ਼ਲੇਸ਼ਣ ਦੇਖਣ ਲਈ ਭਾਸ਼ਾ ਚੁਣੋ?",
      processingDocument: "ਦਸਤਾਵੇਜ਼ ਪ੍ਰੋਸੈਸ ਕੀਤਾ ਜਾ ਰਿਹਾ ਹੈ",
      failedToProcess: "ਰਿਪੋਰਟ ਪ੍ਰੋਸੈਸ ਨਹੀਂ ਹੋਈ। ਕਿਰਪਾ ਕਰਕੇ ਦੁਬਾਰਾ ਕੋਸ਼ਿਸ਼ ਕਰੋ।",
      relatedTopics: "ਸੰਬੰਧਿਤ ਵਿਸ਼ੇ:",
      suggestedQuestions: "ਸੁਝਾਏ ਗਏ ਸਵਾਲ:",
      quickVitalsLog: "ਤੁਰੰਤ ਵਾਇਟਲ ਲਾਗ",
      logDailyHealth: "ਆਪਣੇ ਰੋਜ਼ਾਨਾ ਸਿਹਤ ਮਾਪ ਦਰਜ ਕਰੋ",
      quickHealthActions: "ਤੁਰੰਤ ਸਿਹਤ ਕਾਰਵਾਈ",
      healthToolsSubtitle: "ਆਪਣੇ ਸਿਹਤ ਟੂਲ ਤੁਰੰਤ ਐਕਸੈਸ ਕਰੋ",
      actionVitalReports: "ਵਾਇਟਲ ਰਿਪੋਰਟਾਂ",
      actionVitalReportsDesc: "ਦਿਲ ਦੀ ਧੜਕਣ ਅਤੇ ਬੀਪੀ",
      actionMedication: "ਦਵਾਈ",
      actionMedicationDesc: "ਨੁਸਖੇ",
      actionReports: "ਰਿਪੋਰਟਾਂ",
      actionReportsDesc: "ਮੈਡੀਕਲ ਰਿਕਾਰਡ",
      actionHealthSummary: "ਸਿਹਤ ਸਾਰ",
      actionHealthSummaryDesc: "ਵੈਲਨੈਸ ਝਲਕ",
      actionFamilyMember: "ਪਰਿਵਾਰਕ ਮੈਂਬਰ",
      actionFamilyMemberDesc: "ਪ੍ਰੋਫਾਈਲ ਪ੍ਰਬੰਧਿਤ ਕਰੋ",
      actionAppointments: "ਅਪਾਇੰਟਮੈਂਟ",
      actionAppointmentsDesc: "ਆਉਣ ਵਾਲੀਆਂ ਮੁਲਾਕਾਤਾਂ",
      vitalHeartRate: "ਦਿਲ ਦੀ ਧੜਕਣ",
      vitalBloodPressure: "ਬਲੱਡ ਪ੍ਰੈਸ਼ਰ",
      vitalTemperature: "ਤਾਪਮਾਨ",
      vitalMedicine: "ਦਵਾਈ",
      quickPicks: "ਤੇਜ਼ ਚੋਣਾਂ",
      queryFamily: "ਮੇਰੇ ਪਰਿਵਾਰ ਦੇ ਮੈਂਬਰ ਦਿਖਾਓ",
      queryMedication: "ਮੇਰੀਆਂ ਦਵਾਈਆਂ ਦਿਖਾਓ",
      queryVitals: "ਮੇਰੀ ਵਾਇਟਲ ਰਿਪੋਰਟ ਦਿਖਾਓ",
      queryAppointments: "ਮੇਰੀਆਂ ਮੁਲਾਕாਤਾਂ ਦਿਖਾਓ",
      queryReports: "ਮੇਰੀਆਂ ਰਿপੋਰਟਾਂ ਦਿਖਾਓ",
      querySummary: "ਮੇਰੀ ਸਿਹਤ ਦਾ ਸਾਰ ਦਿਖਾਓ",
      loggedVitals: "ਦਰਜ ਕੀਤਾ ਗਿਆ {title}: {value}",
    },
  },
  te: {
    common: {
      language: "భాష",
      selectLanguage: "భాష ఎంచుకోండి",
      login: "లాగిన్",
      registerNow: "ఇప్పుడే నమోదు చేసుకోండి",
      profile: "ప్రొఫైల్",
      ...buildChangeCredentialLabel("పాస్‌వర్డ్ మార్చండి"),
      logout: "లాగ్ఔట్",
      retry: "మళ్లీ ప్రయత్నించండి",
      notifications: "నోటిఫికేషన్లు",
      self: "స్వయం",
      familyMember: "కుటుంబ సభ్యుడు",
      applyingLanguage: "భాష నవీకరించబడుతోంది…",
    },
    landing: {
      heroTitle: "తారాను కలవండి: ఆసుపత్రి సామర్థ్యానికి తెలివైన కేంద్రం",
      heroDescription:
        "AI ఆధారిత ఎకోసిస్టమ్‌తో వర్క్‌ఫ్లోను సరళీకరించి, రోగి సంరక్షణను మెరుగుపరచండి.",
      getStartedNow: "ఇప్పుడే ప్రారంభించండి",
      learnMore: "ఇంకా తెలుసుకోండి",
      aboutTitle: "ఏకీకృత డిజిటల్ కోర్‌తో ప్రతి ఆసుపత్రిని శక్తివంతం చేయడం",
      faqTitle: "తరచుగా అడిగే ప్రశ్నలు",
    },
    chatbot: {
      welcome: "స్వాగతం! నేను ఎలా సహాయం చేయగలను?",
      welcome_title: "మీ ఆరోగ్య సహాయకుడు",
      welcome_subtitle:
        "లక్షణాలు, మందులు, ల్యాబ్ రిపోర్ట్లు లేదా ఆరోగ్యానికి సంబంధించిన ఏదైనా అడగండి.",
      headerSubtitle: "వీరితో చాట్ చేయండి",
      headerTitle: "ఆన్‌లైన్ ఆరోగ్య సహాయకుడు",
      newChat: "కొత్త చాట్",
      sendMessage: "మీరు ఎలా అనుభూతి చెందుతున్నారో చెప్పండి…",
      selectLanguage: "దయచేసి భాషను ఎంచుకోండి.",
      processingDocument: "డాక్యుమెంట్ ప్రాసెస్ అవుతోంది",
      failedToProcess: "రిపోర్ట్ ప్రాసెస్ కాలేదు. దయచేసి మళ్లీ ప్రయత్నించండి.",
      relatedTopics: "సంబంధిత అంశాలు:",
      suggestedQuestions: "సూచించిన ప్రశ్నలు:",
      quickVitalsLog: "క్విక్ వైటల్స్ లాగ్",
      logDailyHealth: "మీ రోజువారీ ఆరోగ్య మెట్రిక్స్ నమోదు చేయండి",
      quickHealthActions: "త్వరిత ఆరోగ్య చర్యలు",
      healthToolsSubtitle: "మీ ఆరోగ్య సాధనాలను తక్షణం యాక్సెస్ చేయండి",
      actionVitalReports: "వైటల్ రిపోర్ట్లు",
      actionVitalReportsDesc: "గుండె చప్పుడు మరియు బీపీ",
      actionMedication: "మందు",
      actionMedicationDesc: "ప్రిస్క్రిప్షన్లు",
      actionReports: "రిపోర్ట్లు",
      actionReportsDesc: "మెడికల్ రికార్డులు",
      actionHealthSummary: "ఆరోగ్య సారాంశం",
      actionHealthSummaryDesc: "వెల్నెస్ అవలోకనం",
      actionFamilyMember: "కుటుంబ సభ్యుడు",
      actionFamilyMemberDesc: "ప్రొఫైల్‌లను నిర్వహించండి",
      actionAppointments: "అపాయింట్‌మెంట్లు",
      actionAppointmentsDesc: "రాబోయే సందర్శనలు",
      vitalHeartRate: "గుండె చప్పుడు",
      vitalBloodPressure: "రక్తపోటు",
      vitalTemperature: "ఉష్ణోగ్రత",
      vitalMedicine: "మందు",
      quickPicks: "త్వరిత ఎంపికలు",
      queryFamily: "నా కుటుంబ సభ్యులను చూపించు",
      queryMedication: "నా మందులను చూపించు",
      queryVitals: "నా వైటల్ రిపోర్ట్లను చూపించు",
      queryAppointments: "నా అపాయింట్‌మెంట్లను చూపించు",
      queryReports: "నా రిపోర్ట్లను చూపించు",
      querySummary: "నా ఆరోగ్య సారాంశాన్ని చూపించు",
      loggedVitals: "నమోదైంది {title}: {value}",
    },
  },
  ta: {
    common: {
      language: "மொழி",
      selectLanguage: "மொழியை தேர்ந்தெடுக்கவும்",
      login: "உள்நுழை",
      registerNow: "இப்போது பதிவு செய்யவும்",
      profile: "சுயவிவரம்",
      ...buildChangeCredentialLabel("கடவுச்சொல்லை மாற்றவும்"),
      logout: "வெளியேறு",
      retry: "மீண்டும் முயற்சி செய்க",
      notifications: "அறிவிப்புகள்",
      self: "சுயம்",
      familyMember: "குடும்ப உறுப்பினர்",
      applyingLanguage: "மொழி புதுப்பிக்கப்படுகிறது…",
    },
    landing: {
      heroTitle: "தாராவை சந்திக்கவும்: மருத்துவமனை செயல்திறனின் புத்திசாலி மையம்",
      heroDescription:
        "AI அடிப்படையிலான அமைப்புடன் வேலைநடத்தை எளிமைப்படுத்தி நோயாளர் பராமரிப்பை மேம்படுத்துங்கள்.",
      getStartedNow: "இப்போதே தொடங்குங்கள்",
      learnMore: "மேலும் அறிக",
      aboutTitle: "ஒற்றுமையான டிஜிட்டல் மையத்தால் ஒவ்வொரு மருத்துவமனையையும் வலுப்படுத்துதல்",
      faqTitle: "அடிக்கடி கேட்கப்படும் கேள்விகள்",
    },
    chatbot: {
      welcome: "வரவேற்கிறோம்! நான் எப்படி உதவலாம்?",
      welcome_title: "உங்கள் சுகாதார உதவியாளர்",
      welcome_subtitle:
        "அறிகுறிகள், மருந்துகள், ஆய்வக அறிக்கைகள் அல்லது சுகாதாரம் தொடர்பான எதையும் கேளுங்கள்.",
      headerSubtitle: "இவர்களுடன் அரட்டை அடிக்கவும்",
      headerTitle: "ஆன்லைன் சுகாதார உதவியாளர்",
      newChat: "புதிய உரையாடல்",
      sendMessage: "நீங்கள் எப்படி உணர்கிறீர்கள் என்று சொல்லுங்கள்…",
      selectLanguage: "தயவுசெய்து மொழியை தேர்ந்தெடுக்கவும்.",
      processingDocument: "ஆவணம் செயலாக்கப்படுகிறது",
      failedToProcess: "அறிக்கையை செயலாக்க முடியவில்லை. தயவு செய்து மீண்டும் முயற்சிக்கவும்.",
      relatedTopics: "தொடர்புடைய தலைப்புகள்:",
      suggestedQuestions: "பரிந்துரைக்கப்பட்ட கேள்விகள்:",
      quickVitalsLog: "விரைவு வைட்டல்ஸ் பதிவு",
      logDailyHealth: "உங்கள் தினசரி உடல்நல அளவுகளை பதிவு செய்யுங்கள்",
      quickHealthActions: "விரைவு சுகாதார செயல்கள்",
      healthToolsSubtitle: "உங்கள் சுகாதார கருவிகளை உடனடியாக அணுகுங்கள்",
      actionVitalReports: "வைட்டல் அறிக்கைகள்",
      actionVitalReportsDesc: "இதய துடிப்பு மற்றும் பிபி",
      actionMedication: "மருந்து",
      actionMedicationDesc: "மருந்துச்சீட்டுகள்",
      actionReports: "அறிக்கைகள்",
      actionReportsDesc: "மருத்துவ பதிவுகள்",
      actionHealthSummary: "சுகாதார சுருக்கம்",
      actionHealthSummaryDesc: "நல்வாழ்வு கண்ணோட்டம்",
      actionFamilyMember: "குடும்ப உறுப்பினர்",
      actionFamilyMemberDesc: "சுயவிவரங்களை நிர்வகிக்கவும்",
      actionAppointments: "நேரம் பதிவு",
      actionAppointmentsDesc: "வரவிருக்கும் வருகைகள்",
      vitalHeartRate: "இதய துடிப்பு",
      vitalBloodPressure: "இரத்த அழுத்தம்",
      vitalTemperature: "வெப்பநிலை",
      vitalMedicine: "மருந்து",
      quickPicks: "விரைவுத் தேர்வுகள்",
      queryFamily: "என் குடும்ப உறுப்பினர்களைக் காட்டு",
      queryMedication: "என் மருந்துகளைக் காட்டு",
      queryVitals: "என் வைட்டல் அறிக்கைகளைக் காட்டு",
      queryAppointments: "என் சந்திப்புகளைக் காட்டு",
      queryReports: "என் அறிக்கைகளைக் காட்டு",
      querySummary: "என் சுகாதார சுருக்கத்தைக் காட்டு",
      loggedVitals: "பதிவு செய்யப்பட்டது {title}: {value}",
    },
  },
  mr: {
    common: {
      language: "भाषा",
      selectLanguage: "भाषा निवडा",
      login: "लॉगिन",
      registerNow: "आता नोंदणी करा",
      profile: "प्रोफाइल",
      ...buildChangeCredentialLabel("पासवर्ड बदला"),
      logout: "लॉगआउट",
      retry: "पुन्हा प्रयत्न करा",
      notifications: "सूचना",
      self: "स्वतः",
      familyMember: "कुटुंब सदस्य",
      applyingLanguage: "भाषा अपडेट होत आहे…",
    },
    landing: {
      heroTitle: "तारा ला भेटा: हॉस्पिटल कार्यक्षमतेचे बुद्धिमान केंद्र",
      heroDescription:
        "AI-आधारित प्रणालीसह वर्कफ्लो सुलभ करा, रुग्णसेवा वाढवा आणि व्यवस्थापन कार्यभार कमी करा.",
      getStartedNow: "आता सुरू करा",
      learnMore: "अधिक जाणून घ्या",
      aboutTitle: "एकसंध डिजिटल कोरसह प्रत्येक हॉस्पिटलला सक्षम करणे",
      faqTitle: "नेहमी विचारले जाणारे प्रश्न",
    },
    chatbot: {
      welcome: "स्वागत आहे! मी कशी मदत करू?",
      welcome_title: "तुमचा आरोग्य सहाय्यक",
      welcome_subtitle:
        "लक्षणे, औषधे, लॅब अहवाल किंवा आरोग्याशी संबंधित काहीही विचारा.",
      headerSubtitle: "यांच्याशी चॅट करा",
      headerTitle: "ऑनलाइन आरोग्य सहाय्यक",
      newChat: "नवीन चॅट",
      sendMessage: "तुम्हाला कसे वाटत आहे ते सांगा…",
      selectLanguage: "कृपया भाषा निवडा.",
      processingDocument: "दस्तऐवज प्रक्रिया सुरू आहे",
      failedToProcess: "रिपोर्ट प्रक्रिया अयशस्वी झाली. कृपया पुन्हा प्रयत्न करा.",
      relatedTopics: "संबंधित विषय:",
      suggestedQuestions: "सुचवलेले प्रश्न:",
      quickVitalsLog: "त्वरित व्हायटल्स लॉग",
      logDailyHealth: "दररोजचे आरोग्य मेट्रिक्स नोंदवा",
      quickHealthActions: "त्वरित आरोग्य कृती",
      healthToolsSubtitle: "तुमची आरोग्य साधने त्वरित वापरा",
      actionVitalReports: "व्हायटल अहवाल",
      actionVitalReportsDesc: "हृदयगती आणि बीपी",
      actionMedication: "औषध",
      actionMedicationDesc: "प्रिस्क्रिप्शन",
      actionReports: "अहवाल",
      actionReportsDesc: "वैद्यकीय नोंदी",
      actionHealthSummary: "आरोग्य सारांश",
      actionHealthSummaryDesc: "वेलनेस आढावा",
      actionFamilyMember: "कुटुंब सदस्य",
      actionFamilyMemberDesc: "प्रोफाइल व्यवस्थापित करा",
      actionAppointments: "अपॉइंटमेंट",
      actionAppointmentsDesc: "आगामी भेटी",
      vitalHeartRate: "हृदयगती",
      vitalBloodPressure: "रक्तदाब",
      vitalTemperature: "तापमान",
      vitalMedicine: "औषध",
      quickPicks: "त्वरित निवडी",
      queryFamily: "माझ्या कुटुंबातील सदस्य दाखवा",
      queryMedication: "माझी औषधे दाखवा",
      queryVitals: "माझे व्हायटल अहवाल दाखवा",
      queryAppointments: "माझ्या भेटी दाखवा",
      queryReports: "माझे अहवाल दाखवा",
      querySummary: "माझा आरोग्य सारांश दाखवा",
      loggedVitals: "नोंदणीकृत {title}: {value}",
    },
  },
  bn: {
    common: {
      language: "ভাষা",
      selectLanguage: "ভাষা নির্বাচন করুন",
      login: "লগইন",
      registerNow: "এখনই নিবন্ধন করুন",
      profile: "প্রোফাইল",
      ...buildChangeCredentialLabel("পাসওয়ার্ড পরিবর্তন করুন"),
      logout: "লগআউট",
      retry: "আবার চেষ্টা করুন",
      notifications: "নোটিফিকেশন",
      self: "নিজে",
      familyMember: "পরিবারের সদস্য",
      applyingLanguage: "ভাষা আপডেট করা হচ্ছে…",
    },
    landing: {
      heroTitle: "তারার সাথে পরিচিত হোন: হাসপাতাল দক্ষতার বুদ্ধিমান কেন্দ্র",
      heroDescription:
        "AI-চালিত ইকোসিস্টেমের মাধ্যমে ওয়ার্কফ্লো সহজ করুন, রোগী সেবা উন্নত করুন এবং প্রশাসনিক চাপ কমান।",
      getStartedNow: "এখনই শুরু করুন",
      learnMore: "আরও জানুন",
      aboutTitle: "একীভূত ডিজিটাল কোর দিয়ে প্রতিটি হাসপাতালকে সক্ষম করা",
      faqTitle: "প্রায়শই জিজ্ঞাসিত প্রশ্ন",
    },
    chatbot: {
      welcome: "স্বাগতম! আমি কীভাবে সাহায্য করতে পারি?",
      welcome_title: "আপনার স্বাস্থ্য সহায়ক",
      welcome_subtitle:
        "লক্ষণ, ওষুধ, ল্যাব রিপোর্ট বা স্বাস্থ্য সম্পর্কিত যেকোনো বিষয়ে জিজ্ঞাসা করুন।",
      headerSubtitle: "তাদের সাথে চ্যাট করুন",
      headerTitle: "অনলাইন স্বাস্থ্য সহায়ক",
      newChat: "নতুন চ্যাট",
      sendMessage: "আপনি কেমন অনুভব করছেন তা বলুন…",
      selectLanguage: "অনুগ্রহ করে ভাষা নির্বাচন করুন।",
      processingDocument: "ডকুমেন্ট প্রসেস করা হচ্ছে",
      failedToProcess: "রিপোর্ট প্রসেস করা যায়নি। অনুগ্রহ করে আবার চেষ্টা করুন।",
      relatedTopics: "সম্পর্কিত বিষয়:",
      suggestedQuestions: "প্রস্তাবিত প্রশ্ন:",
      quickVitalsLog: "কুইক ভাইটালস লগ",
      logDailyHealth: "আপনার দৈনিক স্বাস্থ্য মেট্রিক্স লগ করুন",
      quickHealthActions: "দ্রুত স্বাস্থ্য কাজ",
      healthToolsSubtitle: "আপনার স্বাস্থ্য সরঞ্জাম তৎক্ষণাত অ্যাক্সেস করুন",
      actionVitalReports: "ভাইটাল রিপোর্ট",
      actionVitalReportsDesc: "হৃদস্পন্দন ও ব্লাড প্রেশার",
      actionMedication: "ওষুধ",
      actionMedicationDesc: "প্রেসক্রিপশন",
      actionReports: "রিপোর্ট",
      actionReportsDesc: "মেডিকেল রেকর্ড",
      actionHealthSummary: "স্বাস্থ্য সারাংশ",
      actionHealthSummaryDesc: "সুস্থতার সংক্ষিপ্ত বিবরণ",
      actionFamilyMember: "পরিবারের সদস্য",
      actionFamilyMemberDesc: "প্রোফাইল পরিচালনা করুন",
      actionAppointments: "অ্যাপয়েন্টমেন্ট",
      actionAppointmentsDesc: "আসন্ন পরিদর্শন",
      vitalHeartRate: "হৃদস্পন্দন",
      vitalBloodPressure: "রক্তচাপ",
      vitalTemperature: "তাপমাত্রা",
      vitalMedicine: "ওষুধ",
      quickPicks: "দ্রুত বাছাই",
      queryFamily: "আমার পরিবারের সদস্যদের দেখান",
      queryMedication: "আমার ওষুধগুলো দেখান",
      queryVitals: "আমার ভাইটাল রিপোর্ট দেখান",
      queryAppointments: "আমার অ্যাপয়েন্টমেন্ট দেখান",
      queryReports: "আমার রিপোর্ট দেখান",
      querySummary: "আমার স্বাস্থ্যের সারাংশ দেখান",
      loggedVitals: "লগ করা হয়েছে {title}: {value}",
    },
  },
  kn: {
    common: {
      language: "ಭಾಷೆ",
      selectLanguage: "ಭಾಷೆಯನ್ನು ಆಯ್ಕೆಮಾಡಿ",
      login: "ಲಾಗಿನ್",
      registerNow: "ಈಗಲೇ ನೋಂದಣಿ ಮಾಡಿ",
      profile: "ಪ್ರೊಫೈಲ್",
      ...buildChangeCredentialLabel("ಪಾಸ್‌ವರ್ಡ್ ಬದಲಿಸಿ"),
      logout: "ಲಾಗೌಟ್",
      retry: "ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ",
      notifications: "ಅಧಿಸೂಚನೆಗಳು",
      self: "ಸ್ವತಃ",
      familyMember: "ಕುಟುಂಬ ಸದಸ್ಯ",
      applyingLanguage: "ಭಾಷೆ ನವೀಕರಿಸಲಾಗುತ್ತಿದೆ…",
    },
    landing: {
      heroTitle: "ತಾರಾ ಪರಿಚಯ: ಆಸ್ಪತ್ರೆ ದಕ್ಷತೆಯ ಬುದ್ಧಿವಂತ ಕೇಂದ್ರ",
      heroDescription:
        "AI ಆಧಾರಿತ ವ್ಯವಸ್ಥೆಯೊಂದಿಗೆ ಕಾರ್ಯಪ್ರವಾಹವನ್ನು ಸರಳಗೊಳಿಸಿ, ರೋಗಿ ಆರೈಕೆಯನ್ನು ಉತ್ತಮಗೊಳಿಸಿ.",
      getStartedNow: "ಈಗಲೇ ಪ್ರಾರಂಭಿಸಿ",
      learnMore: "ಇನ್ನಷ್ಟು ತಿಳಿಯಿರಿ",
      aboutTitle: "ಏಕೀಕೃತ ಡಿಜಿಟಲ್ ಕೋರ್ ಮೂಲಕ ಪ್ರತಿಯೊಂದು ಆಸ್ಪತ್ರೆಗೆ ಶಕ್ತಿ ನೀಡುವುದು",
      faqTitle: "ಪದೇ ಪದೇ ಕೇಳಲಾಗುವ ಪ್ರಶ್ನೆಗಳು",
    },
    chatbot: {
      welcome: "ಸ್ವಾಗತ! ನಾನು ಹೇಗೆ ಸಹಾಯ ಮಾಡಬಹುದು?",
      welcome_title: "ನಿಮ್ಮ ಆರೋಗ್ಯ ಸಹಾಯಕ",
      welcome_subtitle:
        "ಲಕ್ಷಣಗಳು, ಔಷಧಿಗಳು, ಲ್ಯಾಬ್ ವರದಿಗಳು ಅಥವಾ ಆರೋಗ್ಯಕ್ಕೆ ಸಂಬಂಧಿಸಿದ ಯಾವುದನ್ನಾದರೂ ಕೇಳಿ.",
      headerSubtitle: "ಇವರೊಂದಿಗೆ ಚಾಟ್ ಮಾಡಿ",
      headerTitle: "ಆನ್‌ಲೈನ್ ಆರೋಗ್ಯ ಸಹಾಯಕ",
      newChat: "ಹೊಸ ಚಾಟ್",
      sendMessage: "ನೀವು ಹೇಗೆ ಅನುಭವಿಸುತ್ತಿದ್ದೀರಿ ಎಂದು ತಿಳಿಸಿ…",
      selectLanguage: "ದಯವಿಟ್ಟು ಭಾಷೆಯನ್ನು ಆಯ್ಕೆಮಾಡಿ.",
      processingDocument: "ದಸ್ತಾವೇಜು ಪ್ರಕ್ರಿಯೆಯಲ್ಲಿದೆ",
      failedToProcess: "ವರದಿ ಪ್ರಕ್ರಿಯೆ ವಿಫಲವಾಗಿದೆ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ.",
      relatedTopics: "ಸಂಬಂಧಿತ ವಿಷಯಗಳು:",
      suggestedQuestions: "ಸೂಚಿಸಲಾದ ಪ್ರಶ್ನೆಗಳು:",
      quickVitalsLog: "ತ್ವರಿತ ವೈಟಲ್ಸ್ ಲಾಗ್",
      logDailyHealth: "ನಿಮ್ಮ ದೈನಂದಿನ ಆರೋಗ್ಯ ಮಾಪಕಗಳನ್ನು ದಾಖಲಿಸಿ",
      quickHealthActions: "ತ್ವರಿತ ಆರೋಗ್ಯ ಕ್ರಿಯೆಗಳು",
      healthToolsSubtitle: "ನಿಮ್ಮ ಆರೋಗ್ಯ ಸಾಧನಗಳನ್ನು ತಕ್ಷಣ ಪ್ರವೇಶಿಸಿ",
      actionVitalReports: "ವೈಟಲ್ ವರದಿಗಳು",
      actionVitalReportsDesc: "ಹೃದಯ ಬಡಿತ ಮತ್ತು ಬಿಪಿ",
      actionMedication: "ಔಷಧಿ",
      actionMedicationDesc: "ಪ್ರಿಸ್ಕ್ರಿಪ್ಷನ್",
      actionReports: "ವರದಿಗಳು",
      actionReportsDesc: "ವೈದ್ಯಕೀಯ ದಾಖಲೆಗಳು",
      actionHealthSummary: "ಆರೋಗ್ಯ ಸಾರಾಂಶ",
      actionHealthSummaryDesc: "ವೆಲ್ನೆಸ್ ಅವಲೋಕನ",
      actionFamilyMember: "ಕುಟುಂಬ ಸದಸ್ಯ",
      actionFamilyMemberDesc: "ಪ್ರೊಫೈಲ್‌ಗಳನ್ನು ನಿರ್ವಹಿಸಿ",
      actionAppointments: "ಅಪಾಯಿಂಟ್‌ಮೆಂಟ್‌ಗಳು",
      actionAppointmentsDesc: "ಮುಂದಿನ ಭೇಟಿಗಳು",
      vitalHeartRate: "ಹೃದಯ ಬಡಿತ",
      vitalBloodPressure: "ರಕ್ತದೊತ್ತಡ",
      vitalTemperature: "ತಾಪಮಾನ",
      vitalMedicine: "ಔಷಧಿ",
      quickPicks: "ತ್ವರಿತ ಆಯ್ಕೆಗಳು",
      queryFamily: "ನನ್ನ ಕುಟುಂಬದ ಸದಸ್ಯರನ್ನು ತೋರಿಸಿ",
      queryMedication: "ನನ್ನ ಔಷಧಿಗಳನ್ನು ತೋರಿಸಿ",
      queryVitals: "ನನ್ನ ವೈಟಲ್ ವರದಿಗಳನ್ನು ತೋರಿಸಿ",
      queryAppointments: "ನನ್ನ ಅಪಾಯಿಂಟ್‌ಮೆಂಟ್‌ಗಳನ್ನು ತೋರಿಸಿ",
      queryReports: "ನನ್ನ ವರದಿಗಳನ್ನು ತೋರಿಸಿ",
      querySummary: "ನನ್ನ ಆರೋಗ್ಯದ ಸಾರಾಂಶವನ್ನು ತೋರಿಸಿ",
      loggedVitals: "ದಾಖಲಿಸಲಾಗಿದೆ {title}: {value}",
    },
  },
};

applyNavLocaleBlocks(TRANSLATIONS);
applyBreadcrumbLocaleBlocks(TRANSLATIONS);
applyMedCloudLocaleBlocks(TRANSLATIONS);
applyCalendarLocaleBlocks(TRANSLATIONS);
applyReportsLocaleBlocks(TRANSLATIONS);
applyMedicationFormLocaleBlocks(TRANSLATIONS);
applyHelpGuideLocaleBlocks(TRANSLATIONS);
applySettingsLocaleBlocks(TRANSLATIONS);
applyTermsLocaleBlocks(TRANSLATIONS);
applyFamilyPageLocales(TRANSLATIONS);
applyRelationshipTypeLocales(TRANSLATIONS);
applyAuthDashboardLocales(TRANSLATIONS);
applyAiDashboardGoalAndVitalsLocales(TRANSLATIONS);
applyAuthContactLocales(TRANSLATIONS);

export const translate = (language, key, fallback = "") => {
  const resolve = (dict) => key.split(".").reduce((acc, segment) => acc?.[segment], dict);
  const fromPrimary = resolve(TRANSLATIONS[language]);
  if (typeof fromPrimary === "string") return fromPrimary;
  if (language !== "en") {
    if (language !== "hi") {
      const fromHi = resolve(TRANSLATIONS.hi);
      if (typeof fromHi === "string") return fromHi;
    }
    const fromEn = resolve(TRANSLATIONS.en);
    if (typeof fromEn === "string") return fromEn;
  }
  return fallback || key;
};

const humanizeRelationshipType = (relationshipType) => {
  const normalized = String(relationshipType || "").trim();
  if (!normalized) return "";
  const upper = normalized.toUpperCase();
  return upper.charAt(0) + upper.slice(1).toLowerCase();
};

export const translateRelationshipType = (language, relationshipType, fallback = "") => {
  const key = String(relationshipType || "").trim().toUpperCase();
  if (!key) return fallback;
  const translated = translate(language, `common.relationshipTypes.${key}`, "");
  if (translated) return translated;
  return fallback || humanizeRelationshipType(key);
};
